import { WalletError } from '@cef-ai/wallet-api-client';
import type { HostToPopup, PopupToHost } from '../protocol';
import { isMatchingOrigin } from './origin';
import type { PopupMessageHandler } from './types';

export interface PopupHostBridgeOptions {
  /** Expected origin of the host page (the page that opened this popup). */
  hostOrigin: string;
  /** The window this bridge runs in. Defaults to `globalThis`. */
  popupWindow?: Window;
  /** Which window to post PopupToHost messages to. 'opener' (popup, default) or 'parent' (iframe). */
  replyTo?: 'opener' | 'parent';
}

type HandlerMap = {
  [T in HostToPopup['type']]?: PopupMessageHandler<Extract<HostToPopup, { type: T }>>;
};

/**
 * Popup-side bridge. Spec §5.
 *
 * The wallet SPA constructs one of these at boot, registers handlers via
 * `on(type, fn)`, and calls `start()` to begin listening. Each inbound
 * `HostToPopup` message that matches a registered handler is dispatched;
 * the handler is responsible for calling `bridge.send(...)` to reply.
 *
 * Handlers may throw `WalletError`; the bridge automatically converts the
 * throw into a `wallet:error` response keyed by the inbound message id.
 */
export class PopupHostBridge {
  private readonly opts: Required<PopupHostBridgeOptions>;
  private readonly handlers: HandlerMap = {};
  private listener: ((event: MessageEvent) => void) | null = null;

  constructor(options: PopupHostBridgeOptions) {
    this.opts = {
      popupWindow: (globalThis as any).window,
      replyTo: 'opener',
      ...options,
    };
  }

  /** Register a handler for a specific message type. Replaces any prior handler. */
  on<T extends HostToPopup['type']>(type: T, handler: PopupMessageHandler<Extract<HostToPopup, { type: T }>>): this {
    this.handlers[type] = handler as any;
    return this;
  }

  /**
   * Send a `PopupToHost` message back to the opener or parent, depending on
   * `replyTo`. The bridge does not remember the host-window reference itself —
   * it reads the target window each call, so it survives popup re-opens.
   */
  send(message: PopupToHost): void {
    const target: any = this.opts.replyTo === 'parent' ? (globalThis as any).parent : (globalThis as any).opener;
    if (!target || typeof target.postMessage !== 'function') {
      // No target — popup was opened standalone, opener is closed, or not framed.
      return;
    }
    target.postMessage(message, this.opts.hostOrigin);
  }

  /**
   * Start listening for inbound messages.
   *
   * If `announceReady` is provided, sends a `wallet:ready` message immediately.
   * Use this when the popup wants to signal "I'm here" without waiting for the
   * host's first call.
   */
  start(opts: { announceReady?: { id: string } } = {}): void {
    if (this.listener) return;
    this.listener = (event: MessageEvent) => this.onMessage(event);
    this.opts.popupWindow.addEventListener('message', this.listener);
    if (opts.announceReady) {
      this.send({ type: 'wallet:ready', id: opts.announceReady.id });
    }
  }

  stop(): void {
    if (!this.listener) return;
    this.opts.popupWindow.removeEventListener('message', this.listener);
    this.listener = null;
  }

  // ---- internal -------------------------------------------------------------

  private async onMessage(event: MessageEvent): Promise<void> {
    if (!isMatchingOrigin(event.origin, this.opts.hostOrigin)) {
      // Silently drop.
      return;
    }
    const data = event.data as HostToPopup | undefined;
    if (!data || typeof data.type !== 'string' || typeof data.id !== 'string') return;

    const handler = (this.handlers as any)[data.type] as PopupMessageHandler<HostToPopup> | undefined;
    if (!handler) {
      // No handler registered — reply with a typed error.
      this.send({
        type: 'wallet:error',
        id: data.id,
        code: 'internal',
        message: `no handler registered for "${data.type}"`,
      });
      return;
    }

    try {
      await handler(data, { origin: event.origin });
    } catch (err) {
      if (err instanceof WalletError) {
        this.send({
          type: 'wallet:error',
          id: data.id,
          code: err.code,
          message: err.detail ?? '',
          traceId: err.traceId,
        });
      } else {
        this.send({
          type: 'wallet:error',
          id: data.id,
          code: 'internal',
          message: err instanceof Error ? err.message : String(err),
        });
      }
    }
  }
}
